import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AdminUserListResponseDto } from '@lunary/api-client'
import { api } from '../../shared/api/client'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { ADMIN_USERS_KEY } from './queries'
import { UserRow } from './user-row'
import { EMPTY_FILTER, UsersFilters } from './filters'
import type { UsersFilter } from './filters'

const PAGE_SIZE = 50

export function UsersPage() {
  const { user, status } = useAdminAuth()
  const [filter, setFilter] = useState<UsersFilter>(EMPTY_FILTER)
  const [offset, setOffset] = useState(0)
  const isAdmin = status === 'authenticated' && (user?.role === 'admin' || user?.role === 'expert')

  const list = useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'list', filter, offset],
    queryFn: () =>
      api.get<AdminUserListResponseDto>('/admin/users', {
        query: {
          q: filter.q.trim() || undefined,
          role: filter.role || undefined,
          source: filter.source || undefined,
          active_days: filter.activeDays || undefined,
          blocked: filter.blocked || undefined,
          sort: filter.sort,
          limit: PAGE_SIZE,
          offset,
        },
      }),
    enabled: isAdmin,
    retry: false,
  })

  if (!isAdmin) {
    return <p className="error-box">Раздел доступен только сотрудникам.</p>
  }

  const total = list.data?.total ?? 0

  return (
    <div>
      <h1>Пользователи</h1>
      <p className="muted">Кто зарегистрирован, откуда пришёл и когда заходил. Нажмите на имя, чтобы открыть карточку.</p>

      <UsersFilters
        value={filter}
        total={total}
        onChange={(next) => {
          setFilter(next)
          setOffset(0)
        }}
      />

      {list.isPending && <div className="spinner" aria-label="Загрузка" />}
      {list.isError && <p className="error-box">Не удалось загрузить пользователей.</p>}

      {list.data && (
        <div className="card-panel" style={{ overflowX: 'auto' }}>
          <table className="users-table">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Роль</th>
                <th>Пришёл из</th>
                <th>Последняя активность</th>
                <th>Создан</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((item) => (
                <UserRow key={item.id} item={item} isSelf={item.id === user.id} />
              ))}
            </tbody>
          </table>
          {list.data.items.length === 0 && <p className="muted">Никого не нашлось.</p>}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="row">
          <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Назад
          </button>
          <span className="muted">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
          </span>
          <button className="secondary" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Дальше
          </button>
        </div>
      )}
    </div>
  )
}
